import { Alert, AlertTitle, Button, styled, Typography } from '@mui/material';
import { MaterialIcon } from '@common/ui/MaterialIcon';
import { useAponoAuth } from '@common/ui/Authenticated';
import { useUserPassMfa } from '@apono/client-app-sdk';
import { PropsWithChildren, useEffect } from 'react';

export type EnterMfaBannerProps<T = 'multiple'> = T extends 'multiple'
  ? { requestCount?: number; variant?: T }
  : { variant?: T };

const StyledAlert = styled(Alert)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
}));

export function EnterMfaBanner({ requestCount = 1, variant }: EnterMfaBannerProps) {
  const { isMfaPassed } = useAponoAuth();
  const { passMfaRequest } = useUserPassMfa();

  useEffect(() => {
    if (isMfaPassed) {
      passMfaRequest();
    }
  }, [isMfaPassed, passMfaRequest]);

  if (isMfaPassed) {
    return (
      <StyledAlert variant="outlined" severity="success">
        <EnterMfaTitle>MFA passed successfully, processing requests...</EnterMfaTitle>
      </StyledAlert>
    );
  }

  if (variant === 'multiple') {
    return <EnterMfaBannerMultiple requestCount={requestCount} />;
  }

  return <EnterMfaBannerCurrent />;
}

function EnterMfaBannerCurrent() {
  return (
    <StyledAlert variant="outlined" severity="info" action={<EnterMfaBannerButton />}>
      <EnterMfaTitle>This request requires Multi-factor Authentication (MFA)</EnterMfaTitle>
    </StyledAlert>
  );
}

function EnterMfaBannerMultiple({ requestCount }: { requestCount: number }) {
  return (
    <StyledAlert variant="outlined" severity="info" action={<EnterMfaBannerButton />}>
      <EnterMfaTitle>Some requests require Multi-factor Authentication (MFA)</EnterMfaTitle>
      <Typography variant="helperText">
        {`You have ${requestCount} requests that require performing MFA before they can be processed.`}
      </Typography>
    </StyledAlert>
  );
}

function EnterMfaBannerButton() {
  const { onEnterMfa } = useAponoAuth();

  return (
    <Button
      variant="contained"
      startIcon={<MaterialIcon symbol="shield_lock" weight={700} />}
      onClick={onEnterMfa}
      sx={{ marginRight: '12px' }}
    >
      Enter MFA
    </Button>
  );
}

function EnterMfaTitle({ children }: PropsWithChildren) {
  return (
    <AlertTitle variant="subtitle2" marginBottom={0}>
      {children}
    </AlertTitle>
  );
}
